import type {
  GetAllUserTrackingOfGeniallyResponse,
  TrackingDataShortAnswerQuestionResponse,
} from '@genially/contracts';
import { TrackingInteractiveQuestionType } from '@genially/contracts';
import type {
  AnyInteractiveQuestionItemData,
  FillInTheBlanksInteractiveQuestionAnswer,
  FillInTheBlanksInteractiveQuestionItemData,
  ImageInteractiveQuestionAnswer,
  ImageInteractiveQuestionItemData,
  OpenAnswerInteractiveQuestionItemData,
  QuizInteractiveQuestionAnswer,
  QuizInteractiveQuestionItemData,
  ShortAnswerInteractiveQuestionItemData,
  SortInteractiveQuestionAnswer,
  SortInteractiveQuestionItemData,
  SurveyInteractiveQuestionAnswer,
  SurveyInteractiveQuestionItemData,
  TrueFalseInteractiveQuestionItemData,
} from '@genially/document-model';
import { InteractiveQuestionType } from '@genially/document-model';
import { calculate } from '@genially/utils';

import type {
  UserQuestion,
  UserQuestionProps,
} from '../../hooks/useFetchUserTrackingData/models/UserQuestion';
import type { GeniallyContent } from '../../types/GeniallyContent';
import { sortActivitiesBySlideOrder } from './sortActivitiesBySlideOrder';

/**
 * Removes HTML tags from a string and normalizes whitespace.
 *
 * This function takes an HTML string (mainly generated by Remirror),
 * strips all HTML tags and returns the plain text content.
 *
 * @param html - The HTML string to process
 * @returns The plain text content without HTML tags.
 * Returns undefined if the input is undefined.
 */
export const removeRemirrorHtmlTags = (html: string | undefined) => {
  if (!html) {
    return undefined;
  }

  return (
    new DOMParser().parseFromString(
      html
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim(),
      'text/html',
    ).documentElement.textContent || ''
  );
};

type DatedResponse<T> = T & {
  timestamp: number;
};

const processQuizInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<QuizInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
) => {
  const { Id, Question, Answers, IdSlide } = activity;
  const userAnswer = trackingData.answers.find(trackingAnswer => {
    if (trackingAnswer.type === TrackingInteractiveQuestionType.Quiz) {
      const answerExists = Answers.some(
        answer => trackingAnswer.answerIds && answer.Id === trackingAnswer.answerIds[0],
      );

      return trackingAnswer.interactiveQuestionId === Id && answerExists;
    }

    return false;
  });

  const isTimeFinished = Boolean(
    trackingData.answers.find(trackingAnswer => {
      if (
        trackingAnswer.type === TrackingInteractiveQuestionType.Quiz &&
        trackingAnswer.interactiveQuestionId === Id
      ) {
        return trackingAnswer.answerIds && trackingAnswer.answerIds.length === 0;
      }

      return false;
    }),
  );

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.Quiz,
    question: Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.Quiz) {
    const userAnswers = userAnswer.answerIds
      ?.map(userAnswerId => Answers.find(({ Id: answerId }) => userAnswerId === answerId))
      .filter(answer => !!answer) as QuizInteractiveQuestionAnswer[];

    const userAnswerTexts = userAnswers.map(({ Text }) => Text);
    const userAnswerIds = userAnswers.map(({ Id }) => Id);

    const correctAnswers = Answers.filter(({ IsCorrect }) => IsCorrect);

    const isCorrect =
      userAnswers.every(({ IsCorrect }) => IsCorrect) &&
      userAnswers.length === correctAnswers.length;

    userQuestion = {
      ...userQuestion,
      isCorrect,
      answer: userAnswerTexts,
      answersSelected: userAnswerIds,
      sentDate: new Date(userAnswer.timestamp),
    };
  }

  return userQuestion;
};

const processOpenAnswerInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<OpenAnswerInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
) => {
  const { Id, Question, IdSlide } = activity;

  const userAnswer = trackingData.answers.find(
    ({ interactiveQuestionId }) => interactiveQuestionId === Id,
  );

  const isTimeFinished = Boolean(
    trackingData.answers.find(trackingAnswer => {
      if (
        trackingAnswer.type === TrackingInteractiveQuestionType.OpenAnswer &&
        trackingAnswer.interactiveQuestionId === Id
      ) {
        return trackingAnswer.answerText === '';
      }

      return false;
    }),
  );

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.OpenAnswer,
    question: Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.OpenAnswer) {
    userQuestion = {
      ...userQuestion,
      answer: [userAnswer?.answerText],
      sentDate: userAnswer ? new Date(userAnswer.timestamp) : undefined,
      answersSelected: userAnswer?.answerText ? [userAnswer?.answerText] : undefined,
    };
  }

  return userQuestion;
};

const processTrueFalseInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<TrueFalseInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
) => {
  const { Id, Question, Answers, IdSlide } = activity;
  const userAnswer = trackingData.answers.find(trackingAnswer => {
    if (trackingAnswer.type === TrackingInteractiveQuestionType.TrueFalse) {
      const answerExists = Answers.some(
        answer => trackingAnswer.answerIds && answer.Id === trackingAnswer.answerIds[0],
      );

      return trackingAnswer.interactiveQuestionId === Id && answerExists;
    }

    return false;
  });

  const isTimeFinished = Boolean(
    trackingData.answers.find(trackingAnswer => {
      if (
        trackingAnswer.type === TrackingInteractiveQuestionType.TrueFalse &&
        trackingAnswer.interactiveQuestionId === Id
      ) {
        return trackingAnswer.answerIds && trackingAnswer.answerIds.length === 0;
      }

      return false;
    }),
  );

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.TrueFalse,
    question: Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.TrueFalse) {
    const answer = Answers.find(
      ({ Id: answerId }) => userAnswer.answerIds && userAnswer.answerIds[0] === answerId,
    );

    userQuestion = {
      ...userQuestion,
      answer: answer?.Text ? [answer.Text] : [],
      answersSelected: answer?.Id ? [answer.Id] : [],
      isCorrect: answer?.IsCorrect,
      sentDate: new Date(userAnswer.timestamp),
    };
  }

  return userQuestion;
};

const processImageInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<ImageInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
) => {
  const { Id, Question, Answers, IdSlide } = activity;
  const userAnswer = trackingData.answers.find(trackingAnswer => {
    if (trackingAnswer.type === TrackingInteractiveQuestionType.Image) {
      const answerExists = Answers.some(
        answer => trackingAnswer.answerIds && answer.Id === trackingAnswer.answerIds[0],
      );

      return trackingAnswer.interactiveQuestionId === Id && answerExists;
    }

    return false;
  });

  const isTimeFinished = Boolean(
    trackingData.answers.find(trackingAnswer => {
      if (
        trackingAnswer.type === TrackingInteractiveQuestionType.Image &&
        trackingAnswer.interactiveQuestionId === Id
      ) {
        return trackingAnswer.answerIds && trackingAnswer.answerIds.length === 0;
      }

      return false;
    }),
  );

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.Image,
    question: Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.Image) {
    const userAnswers = userAnswer.answerIds
      ?.map(userAnswerId => Answers.find(({ Id: answerId }) => userAnswerId === answerId))
      .filter(answer => !!answer) as ImageInteractiveQuestionAnswer[];

    const userAnswerTexts = userAnswers.map(({ Text }) => Text);
    const userAnswerIds = userAnswers.map(({ Id }) => Id);

    const correctAnswers = Answers.filter(({ IsCorrect }) => IsCorrect);

    const isCorrect =
      userAnswers.every(({ IsCorrect }) => IsCorrect) &&
      userAnswers.length === correctAnswers.length;

    userQuestion = {
      ...userQuestion,
      isCorrect,
      answer: userAnswerTexts,
      answersSelected: userAnswerIds,
      sentDate: new Date(userAnswer.timestamp),
    };
  }

  return userQuestion;
};

const processSortInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<SortInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
) => {
  const { Id, Question, Answers, IdSlide } = activity;
  const userAnswer = trackingData.answers.find(trackingAnswer => {
    if (trackingAnswer.type === TrackingInteractiveQuestionType.Sort) {
      const answerExists = Answers.some(
        answer => trackingAnswer.answerIds && answer.Id === trackingAnswer.answerIds[0],
      );

      return trackingAnswer.interactiveQuestionId === Id && answerExists;
    }

    return false;
  });

  const isTimeFinished = Boolean(
    trackingData.answers.find(trackingAnswer => {
      if (
        trackingAnswer.type === TrackingInteractiveQuestionType.Sort &&
        trackingAnswer.interactiveQuestionId === Id
      ) {
        return trackingAnswer.answerIds && trackingAnswer.answerIds.length === 0;
      }

      return false;
    }),
  );

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.Sort,
    question: Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.Sort) {
    const userAnswers = userAnswer.answerIds
      ?.map(userAnswerId => Answers.find(({ Id: answerId }) => userAnswerId === answerId))
      .filter(answer => !!answer) as SortInteractiveQuestionAnswer[];

    const userAnswerTexts = userAnswers.map(({ Text }) => Text);
    const userAnswerIds = userAnswers.map(({ Id }) => Id);

    const isCorrect = userAnswers.every(
      ({ CorrectOrder }, index) => CorrectOrder === index,
    );

    userQuestion = {
      ...userQuestion,
      isCorrect,
      answer: userAnswerTexts,
      answersSelected: userAnswerIds,
      sentDate: new Date(userAnswer.timestamp),
    };
  }

  return userQuestion;
};

const processSurveyInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<SurveyInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
) => {
  const { Id, Question, Answers, IdSlide } = activity;
  const userAnswer = trackingData.answers.find(trackingAnswer => {
    if (trackingAnswer.type === TrackingInteractiveQuestionType.Survey) {
      const answerExists = Answers.some(
        answer => trackingAnswer.answerIds && answer.Id === trackingAnswer.answerIds[0],
      );

      return trackingAnswer.interactiveQuestionId === Id && answerExists;
    }

    return false;
  });

  const isTimeFinished = Boolean(
    trackingData.answers.find(trackingAnswer => {
      if (
        trackingAnswer.type === TrackingInteractiveQuestionType.Survey &&
        trackingAnswer.interactiveQuestionId === Id
      ) {
        return trackingAnswer.answerIds && trackingAnswer.answerIds.length === 0;
      }

      return false;
    }),
  );

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.Survey,
    question: Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.Survey) {
    const userAnswers = userAnswer.answerIds
      ?.map(userAnswerId => Answers.find(({ Id: answerId }) => userAnswerId === answerId))
      .filter(answer => !!answer) as SurveyInteractiveQuestionAnswer[];

    const userAnswerTexts = userAnswers.map(({ Text }) => Text);
    const userAnswerIds = userAnswers.map(({ Id }) => Id);

    userQuestion = {
      ...userQuestion,
      answer: userAnswerTexts,
      answersSelected: userAnswerIds,
      sentDate: new Date(userAnswer.timestamp),
    };
  }

  return userQuestion;
};

const processFillInTheBlanksInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<FillInTheBlanksInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
  removeHtmlTags = false,
) => {
  const { Id, Question, Answers, IdSlide } = activity;
  const userAnswer = trackingData.answers.find(trackingAnswer => {
    if (trackingAnswer.type === TrackingInteractiveQuestionType.FillInTheBlanks) {
      const answerExists = Answers.some(answer =>
        Object.values(trackingAnswer.userAnswer ?? []).includes(answer.Id),
      );

      return trackingAnswer.interactiveQuestionId === Id && answerExists;
    }

    return false;
  });

  const isTimeFinished = Boolean(
    trackingData.answers.find(trackingAnswer => {
      if (
        trackingAnswer.type === TrackingInteractiveQuestionType.FillInTheBlanks &&
        trackingAnswer.interactiveQuestionId === Id
      ) {
        return (
          trackingAnswer.userAnswer && Object.keys(trackingAnswer.userAnswer).length === 0
        );
      }

      return false;
    }),
  );

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.FillInTheBlanks,
    question: removeHtmlTags ? removeRemirrorHtmlTags(Question) : Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.FillInTheBlanks) {
    const userAnswers = Object.values(userAnswer.userAnswer)
      .map(userAnswerId => Answers.find(({ Id: answerId }) => userAnswerId === answerId))
      .filter(answer => !!answer) as FillInTheBlanksInteractiveQuestionAnswer[];
    const userAnswersSorted = userAnswers.sort((a, b) => a.CorrectOrder - b.CorrectOrder);

    const userAnswerTexts = userAnswersSorted.map(({ Text }) => Text);
    const userAnswerIds = userAnswersSorted.map(({ Id }) => Id);

    // userAnswerIds.includes(answerId) checks whether the answerId selected by the user exists
    // in the content of the genially. This way, the user's answer is compared with the content
    // of the Genially, just like with the other interactive questions.
    const isCorrect = Object.entries(userAnswer.userAnswer).every(
      ([blankId, answerId]) => blankId === answerId && userAnswerIds.includes(answerId),
    );

    userQuestion = {
      ...userQuestion,
      isCorrect,
      answer: userAnswerTexts,
      answersSelected: userAnswerIds,
      sentDate: new Date(userAnswer.timestamp),
    };
  }

  return userQuestion;
};

const processShortAnswerInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData<ShortAnswerInteractiveQuestionItemData>,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
  removeHtmlTags = false,
) => {
  const { Id, Question, Answers, IdSlide, Options } = activity;
  const userAnswer = trackingData.answers.find(trackingAnswer => {
    return (
      trackingAnswer.type === TrackingInteractiveQuestionType.ShortAnswer &&
      trackingAnswer.interactiveQuestionId === Id
    );
  }) as DatedResponse<TrackingDataShortAnswerQuestionResponse> | undefined;

  const isTimeFinished = !!userAnswer && userAnswer.answerText === '';

  let userQuestion: UserQuestionProps = {
    id: Id,
    type: InteractiveQuestionType.ShortAnswer,
    question: removeHtmlTags ? removeRemirrorHtmlTags(Question) : Question,
    slideId: IdSlide,
    userAlias: trackingData.userAlias,
    isTimeFinished,
  };

  const normalizeString = (str: string): string => {
    return str
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  if (userAnswer && userAnswer.type === TrackingInteractiveQuestionType.ShortAnswer) {
    const isCorrect = calculate(() => {
      if (Options.AcceptVariations) {
        const parsedAnswerText = normalizeString(userAnswer.answerText);

        return Answers.some(answer => normalizeString(answer.Text) === parsedAnswerText);
      }

      return Answers.some(answer => answer.Text.trim() === userAnswer.answerText.trim());
    });

    userQuestion = {
      ...userQuestion,
      isCorrect,
      answer: [userAnswer.answerText],
      sentDate: new Date(userAnswer.timestamp),
      answersSelected: [userAnswer?.answerText],
    };
  }

  return userQuestion;
};

const processInteractiveQuestion = (
  activity: AnyInteractiveQuestionItemData,
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
  removeHtmlTags = false,
): UserQuestionProps | undefined => {
  switch (activity.Type) {
    case InteractiveQuestionType.Quiz:
      return processQuizInteractiveQuestion(activity, trackingData);

    case InteractiveQuestionType.OpenAnswer:
      return processOpenAnswerInteractiveQuestion(activity, trackingData);

    case InteractiveQuestionType.TrueFalse:
      return processTrueFalseInteractiveQuestion(activity, trackingData);

    case InteractiveQuestionType.Image:
      return processImageInteractiveQuestion(activity, trackingData);

    case InteractiveQuestionType.Sort:
      return processSortInteractiveQuestion(activity, trackingData);

    case InteractiveQuestionType.Survey:
      return processSurveyInteractiveQuestion(activity, trackingData);

    case InteractiveQuestionType.FillInTheBlanks:
      return processFillInTheBlanksInteractiveQuestion(
        activity,
        trackingData,
        removeHtmlTags,
      );

    case InteractiveQuestionType.ShortAnswer:
      return processShortAnswerInteractiveQuestion(
        activity,
        trackingData,
        removeHtmlTags,
      );

    default:
      return undefined;
  }
};

export const processQuestions = (
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
  geniallyContent: GeniallyContent,
  removeHtmlTags = false,
): UserQuestionProps[] => {
  const sortedActivities = sortActivitiesBySlideOrder(geniallyContent);

  return sortedActivities
    .map(activity => processInteractiveQuestion(activity, trackingData, removeHtmlTags))
    .filter(userQuestion => userQuestion !== undefined) as UserQuestion[];
};

export const processAttempts = (
  trackingData: GetAllUserTrackingOfGeniallyResponse['data'][number],
  geniallyContent: GeniallyContent,
) => {
  const sortedActivities = sortActivitiesBySlideOrder(geniallyContent);

  return trackingData.answers
    .map((answer, index) => {
      const activity = sortedActivities.find(a => a.Id === answer.interactiveQuestionId);
      const trackingDataWithoutCurrentAnswer = trackingData.answers.filter(
        (a, i) =>
          a.interactiveQuestionId !== answer.interactiveQuestionId ||
          (a.interactiveQuestionId === answer.interactiveQuestionId && index === i),
      );
      if (activity) {
        return processInteractiveQuestion(activity, {
          ...trackingData,
          answers: trackingDataWithoutCurrentAnswer,
        });
      }
      return undefined;
    })
    .filter(userQuestion => userQuestion !== undefined) as UserQuestion[];
};
